/**
 * ResourceRequestPlanModal
 *
 * Full-screen centred modal for the Suggested Project Plan.
 * Left panel: request summary (fixed). Right panel: scenarios (scrollable).
 * Each proposed booking slot is editable — resource + dates can be adjusted
 * before accepting. Edits are local to the modal session.
 * Evo Design System — semantic tokens only.
 */

import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  User,
  Users,
  Zap,
  CheckCircle2,
  CalendarDays,
  ChevronDown,
  ThumbsUp,
  ThumbsDown,
  ArrowRight,
  Building2,
  Tag,
  Loader2,
  Check,
  Star,
  Clock,
  Pencil,
  RotateCcw,
  Info,
  Eye,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  ResourceRequest,
  PlanScenario,
  PlanPreviewScenario,
  ProposedBookingSlot,
  ScenarioStrategy,
} from '@/types/resourceRequest';
import { MatchScoreBadge } from '@/components/booking/MatchScoreBadge';
import { Resource, Booking } from '@/types';
import { computeRecommendations } from '@/lib/recommendationEngine';
import { MatchSignal } from '@/types/recommendation';
import {
  useProjectBudget,
  findRoleBudget,
  parseMonthlyHours,
  budgetHealthAccent,
} from '@/hooks/useProjectBudget';
import type { EBProjectsBudgetComposite } from '@/services/api';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso: string) {
  try { return format(parseISO(iso), 'd MMM yyyy'); } catch (_e) { return iso; }
}

function initials(name: string) {
  return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

// ── Slot edit state ───────────────────────────────────────────────────────────

interface SlotEdit {
  resourceId?:      string;
  resourceName?:    string;
  resourcePhotoUrl?: string;
  startDate?:       string;
  endDate?:         string;
  hours?:           number;
}

type SlotEdits = Map<string, SlotEdit>;

function mergeSlot(slot: ProposedBookingSlot, edit?: SlotEdit): ProposedBookingSlot {
  if (!edit) return slot;
  return { ...slot, ...edit };
}

// ── Score breakdown popover ───────────────────────────────────────────────────

const SIGNAL_META: Record<MatchSignal['key'], { emoji: string; label: string }> = {
  availability: { emoji: '📅', label: 'Availability'  },
  utilisation:  { emoji: '⚡', label: 'Utilisation'   },
  role:         { emoji: '🎯', label: 'Role Match'    },
  skills:       { emoji: '🛠', label: 'Skills Match'  },
  location:     { emoji: '📍', label: 'Location'      },
  budget:       { emoji: '💰', label: 'Budget Fit'    },
};

function ScoreBreakdownTooltip({ signals, score, children }: {
  signals:  MatchSignal[];
  score:    number;
  children: React.ReactNode;
}) {
  const sorted = [...signals].sort((a, b) => (b.weight * b.score) - (a.weight * a.score));

  const overallColor =
    score >= 75 ? 'text-[var(--accent-teal-700)] bg-[var(--accent-teal-50)] border-[var(--accent-teal-300)]' :
    score >= 45 ? 'text-[var(--accent-warning-700)] bg-[var(--accent-warning-50)] border-[var(--accent-warning-300)]' :
                  'text-destructive bg-destructive/5 border-destructive/30';

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild onClick={e => e.stopPropagation()}>
          {children as React.ReactElement}
        </TooltipTrigger>
        <TooltipPrimitive.Portal>
        <TooltipContent
          side="right"
          align="center"
          sideOffset={16}
          avoidCollisions={false}
          className="p-0 w-80 shadow-2xl border border-border/80 rounded-xl overflow-hidden bg-popover !z-[9999]"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border/60 bg-muted/30">
            <div>
              <p className="text-xs font-semibold text-foreground">Match Score Breakdown</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">Weighted across 6 signals</p>
            </div>
            <span className={cn(
              'text-sm font-bold px-2.5 py-1 rounded-lg border tabular-nums',
              overallColor,
            )}>
              {score}%
            </span>
          </div>

          {/* Signal rows */}
          <div className="px-4 py-3 space-y-3">
            {sorted.map(sig => {
              const pct          = Math.round(sig.score);
              const contribution = Math.round(sig.score * sig.weight);
              const meta         = SIGNAL_META[sig.key];
              const barColor =
                pct >= 75 ? 'bg-[var(--accent-teal-500)]' :
                pct >= 45 ? 'bg-[var(--accent-warning-500)]' :
                            'bg-destructive/60';
              const textColor =
                pct >= 75 ? 'text-[var(--accent-teal-700)]' :
                pct >= 45 ? 'text-[var(--accent-warning-700)]' :
                            'text-destructive';

              return (
                <div key={sig.key} className="space-y-1.5">
                  {/* Label row */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm leading-none">{meta.emoji}</span>
                      <span className="text-xs font-medium text-foreground">{meta.label}</span>
                    </div>
                    <div className="flex items-center gap-2.5 shrink-0">
                      <span className="text-[10px] text-muted-foreground tabular-nums">{contribution}pts</span>
                      <span className={cn('text-xs font-bold tabular-nums w-9 text-right', textColor)}>
                        {pct}%
                      </span>
                    </div>
                  </div>
                  {/* Bar */}
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className={cn('h-full rounded-full transition-all duration-500', barColor)}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  {/* Explanation */}
                  <p className="text-[10px] text-muted-foreground leading-relaxed pl-6">
                    {sig.explanation}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="px-4 py-2.5 border-t border-border/60 bg-muted/20">
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              Higher weight signals have more influence on the overall match score.
            </p>
          </div>
        </TooltipContent>
        </TooltipPrimitive.Portal>
      </Tooltip>
    </TooltipProvider>
  );
}

// ── Strategy config ───────────────────────────────────────────────────────────

interface StrategyMeta {
  icon:        React.ReactNode;
  accent:      string;
  ringAccent:  string;
  recommended?: boolean;
}

const STRATEGY_META: Record<ScenarioStrategy, StrategyMeta> = {
  'single-resource': {
    icon:      <User className="h-4 w-4" />,
    accent:    'accent-purple',
    ringAccent:'ring-[var(--accent-purple-500)]',
  },
  'spread-load': {
    icon:      <Users className="h-4 w-4" />,
    accent:    'accent-teal',
    ringAccent:'ring-[var(--accent-teal-500)]',
    recommended: true,
  },
  'fastest-start': {
    icon:      <Zap className="h-4 w-4" />,
    accent:    'accent-warning',
    ringAccent:'ring-[var(--accent-warning-500)]',
  },
};

// ── Editable Slot Row ─────────────────────────────────────────────────────────

interface SlotRowProps {
  slot:            ProposedBookingSlot;
  edit?:           SlotEdit;
  resources:       Resource[];
  existingBookings: Booking[];
  requestSkills:   string[];
  isEditing:       boolean;
  onEdit:          (id: string, patch: SlotEdit) => void;
  onReset:         (id: string) => void;
}

// ── Daily breakdown summary (visible inline) ─────────────────────────────────
function formatTime(isoStr: string): string {
  try {
    return formatInTimeZone(parseISO(isoStr), 'UTC', 'HH:mm');
  } catch { return ''; }
}

function DailyBreakdownSummary({ slot }: { slot: ProposedBookingSlot }) {
  const breakdown = slot.dailyBreakdown;
  if (!breakdown || breakdown.length === 0) return null;

  const summaryLines = breakdown.map(d => {
    try {
      const dt = parseISO(d.date);
      const dateLabel = format(dt, 'd MMM');
      const timeRange = d.startTime && d.endTime
        ? ` (${formatTime(d.startTime)}–${formatTime(d.endTime)})`
        : '';
      return { dateLabel, hours: d.hours, timeRange };
    } catch { return { dateLabel: d.date, hours: d.hours, timeRange: '' }; }
  });

  return (
    <div className="mt-1.5 space-y-0.5">
      <div className="flex items-center gap-1 text-[10px] font-medium text-muted-foreground">
        <CalendarDays className="h-2.5 w-2.5 shrink-0" />
        <span>{breakdown.length} day{breakdown.length !== 1 ? 's' : ''} planned</span>
      </div>
      <div className="pl-3.5 space-y-0 text-[10px] text-muted-foreground">
        {summaryLines.map((line, i) => (
          <p key={i}>{line.dateLabel}: {line.hours}h{line.timeRange}</p>
        ))}
      </div>
    </div>
  );
}

// ── Read-only slot row (no hooks beyond merged/isDirty) ───────────────────────
function SlotRowReadOnly({ slot, edit }: { slot: ProposedBookingSlot; edit?: SlotEdit }) {
  const merged  = mergeSlot(slot, edit);
  const isDirty = !!edit && Object.keys(edit).length > 0;
  return (
    <div className={cn(
      'flex items-center gap-3 py-2 px-3 rounded-lg border transition-colors',
      isDirty ? 'bg-primary/5 border-primary/20' : 'bg-background/60 border-border/40',
    )}>
      <Avatar className="h-7 w-7 rounded-lg shrink-0">
        <AvatarImage src={merged.resourcePhotoUrl} className="rounded-lg" />
        <AvatarFallback className="rounded-lg text-[10px] bg-muted font-medium">
          {initials(merged.resourceName)}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-medium truncate">{merged.resourceName}</span>
          {isDirty && (
            <span className="text-[9px] font-semibold px-1 py-0.5 rounded bg-primary/10 text-primary leading-none">EDITED</span>
          )}
        </div>
        <div className="flex items-center gap-1 mt-0.5 text-[10px] text-muted-foreground">
          <span>{fmtDate(merged.startDate)}</span>
          <ArrowRight className="h-2.5 w-2.5 shrink-0" />
          <span>{fmtDate(merged.endDate)}</span>
        </div>
        <DailyBreakdownSummary slot={slot} />
      </div>
      <span className="text-xs font-bold tabular-nums shrink-0">{merged.hours}h</span>
      <MatchScoreBadge score={slot.matchScore} />
    </div>
  );
}

// ── Editable slot row (hooks for dropdown + recommendation engine) ─────────────
function SlotRowEditing({ slot, edit, resources, existingBookings, requestSkills, onEdit, onReset }: Omit<SlotRowProps, 'isEditing'>) {
  const merged  = mergeSlot(slot, edit);
  const isDirty = !!edit && Object.keys(edit).length > 0;
  const [dropOpen, setDropOpen] = useState(false);
  const dropRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) {
        setDropOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropOpen]);

  // Score & rank all resources — only runs when this component is mounted (edit mode)
  const rankedResources = useMemo(() => {
    try {
      const start = parseISO(merged.startDate);
      const end   = parseISO(merged.endDate);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) throw new Error('invalid dates');
      const result = computeRecommendations({
        requiredRole:    slot.resourceRole ?? '',
        requiredSkills:  requestSkills ?? [],
        startDate:       start,
        endDate:         end,
        location:        '',
        allResources:    resources,
        existingBookings,
        projects:        [],
        projectId:       null,
      });
      const all = [...result.cleanMatches, ...result.conflictMatches];
      return all.map(m => ({
        resource: m.resource,
        score:    m.overallScore,
        avail:    Math.round(m.signals.find(s => s.key === 'availability')?.score ?? 0),
        signals:  m.signals,
      }));
    } catch (_e) {
      return (resources ?? []).map(r => ({ resource: r, score: 0, avail: 0, signals: [] as MatchSignal[] }));
    }
  }, [resources, existingBookings, requestSkills, slot.resourceRole, merged.startDate, merged.endDate]);

  return (
    <div className={cn(
      'rounded-lg border transition-colors',
      isDirty ? 'bg-primary/5 border-primary/20' : 'bg-muted/15 border-border/40',
    )}>
      {/* Resource row */}
      <div className="flex items-center gap-2 px-3 pt-2.5 pb-2">
        <Avatar className="h-6 w-6 rounded-md shrink-0">
          <AvatarImage src={merged.resourcePhotoUrl} className="rounded-md" />
          <AvatarFallback className="rounded-md text-[9px] bg-muted font-medium">
            {initials(merged.resourceName)}
          </AvatarFallback>
        </Avatar>

        {/* Resource dropdown */}
        <div ref={dropRef} className="relative flex-1 min-w-0">
          <button
            type="button"
            onClick={() => setDropOpen(v => !v)}
            className="w-full h-7 flex items-center justify-between gap-2 rounded-md border border-input bg-background px-2.5 text-xs hover:bg-accent/30 transition-colors"
          >
            <span className="truncate font-medium">{merged.resourceName}</span>
            <ChevronDown className={cn('h-3 w-3 text-muted-foreground shrink-0 transition-transform', dropOpen && 'rotate-180')} />
          </button>

          {dropOpen && (
            <div className="absolute top-full left-0 mt-1 z-50 w-[360px] rounded-lg border bg-popover shadow-lg overflow-hidden">
              <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/40">
                <div className="flex-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Resource</div>
                <div className="w-12 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide text-right">Avail.</div>
                <div className="w-14 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide text-right">Match</div>
              </div>
              <div className="max-h-64 overflow-y-auto">
                {rankedResources.map(({ resource: r, score, avail, signals }) => {
                  const rid       = String(r.id);
                  const isCurrent = rid === merged.resourceId;
                  return (
                    <div
                      key={rid}
                      className={cn(
                        'flex items-center gap-2.5 px-3 py-2 transition-colors hover:bg-accent/40 cursor-pointer',
                        isCurrent && 'bg-primary/5',
                      )}
                      onClick={() => {
                        onEdit(slot.id, { resourceId: rid, resourceName: r.name, resourcePhotoUrl: r.photoUrl });
                        setDropOpen(false);
                      }}
                    >
                      {/* Avatar */}
                      <Avatar className="h-6 w-6 rounded-md shrink-0">
                        <AvatarImage src={r.photoUrl} className="rounded-md" />
                        <AvatarFallback className="rounded-md text-[9px] bg-muted font-medium">
                          {initials(r.name)}
                        </AvatarFallback>
                      </Avatar>

                      {/* Name + role */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <span className="text-xs font-medium truncate">{r.name}</span>
                          {isCurrent && <Check className="h-3 w-3 text-primary shrink-0" />}
                        </div>
                        {r.role && (
                          <span className="text-[10px] text-muted-foreground leading-none">{r.role}</span>
                        )}
                      </div>

                      {/* Availability */}
                      <div className="w-12 text-right shrink-0">
                        <span className={cn(
                          'text-xs font-semibold tabular-nums',
                          avail >= 50 ? 'text-[var(--accent-teal-600)]' :
                          avail >= 20 ? 'text-[var(--accent-warning-600)]' :
                                        'text-destructive',
                        )}>
                          {avail}%
                        </span>
                      </div>

                      {/* Score + hover tooltip — stop propagation so tooltip doesn't trigger row click */}
                      <div
                        className="flex items-center gap-1 w-16 justify-end shrink-0"
                        onClick={e => e.stopPropagation()}
                      >
                        <MatchScoreBadge score={score} />
                        {signals.length > 0 && (
                          <ScoreBreakdownTooltip score={score} signals={signals}>
                            <button
                              type="button"
                              className="text-muted-foreground/50 hover:text-primary transition-colors p-0.5 rounded"
                            >
                              <Info className="h-3.5 w-3.5" />
                            </button>
                          </ScoreBreakdownTooltip>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Reset button */}
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={e => { e.stopPropagation(); onReset(slot.id); }}
                className={cn(
                  'p-1 rounded transition-colors shrink-0',
                  isDirty ? 'text-primary hover:bg-muted cursor-pointer' : 'text-muted-foreground/25 cursor-default pointer-events-none',
                )}
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            {isDirty && <TooltipContent side="top" className="text-xs">Reset to suggested</TooltipContent>}
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Date + hours row */}
      <div className="flex items-center gap-2 px-3 pb-2.5 border-t border-border/30 pt-2">
        <Input
          type="date"
          className="h-7 text-xs flex-1"
          value={merged.startDate}
          onChange={e => onEdit(slot.id, { startDate: e.target.value })}
        />
        <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
        <Input
          type="date"
          className="h-7 text-xs flex-1"
          value={merged.endDate}
          onChange={e => onEdit(slot.id, { endDate: e.target.value })}
        />
        <div className="flex items-center gap-1 shrink-0">
          <Input
            type="number"
            min={1}
            max={slot.availableHours}
            className="h-7 text-xs w-14 text-center"
            value={merged.hours}
            onChange={e => onEdit(slot.id, { hours: Number(e.target.value) })}
          />
          <span className="text-[10px] text-muted-foreground">h</span>
        </div>
        <MatchScoreBadge score={slot.matchScore} />
      </div>

      {/* Daily breakdown (visible when not edited) */}
      {!isDirty && <div className="px-3 pb-2"><DailyBreakdownSummary slot={slot} /></div>}
    </div>
  );
}

// ── Dispatcher: renders read-only or editable version ─────────────────────────
function SlotRow({ slot, edit, resources, existingBookings, requestSkills, isEditing, onEdit, onReset }: SlotRowProps) {
  if (!isEditing) {
    return <SlotRowReadOnly slot={slot} edit={edit} />;
  }
  return (
    <SlotRowEditing
      slot={slot} edit={edit}
      resources={resources} existingBookings={existingBookings}
      requestSkills={requestSkills}
      onEdit={onEdit} onReset={onReset}
    />
  );
}

// ── Scenario card ─────────────────────────────────────────────────────────────

// ── Preview button (standalone so it doesn't re-render the full card) ─────────

interface PreviewButtonProps {
  scenario: PlanScenario;
  onPreview: (scenario: PlanScenario) => void;
}

function PreviewButton({ scenario, onPreview }: PreviewButtonProps) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-6 text-[10px] px-2 gap-1 shrink-0"
            onClick={(e) => { e.stopPropagation(); onPreview(scenario); }}
          >
            <Eye className="h-3 w-3" />
            Preview on calendar
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs max-w-[200px]">
          See these proposed bookings overlaid on your current calendar view before accepting
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface ScenarioCardProps {
  scenario:              PlanScenario;
  isSelected:            boolean;
  isAccepted?:           boolean;
  resources:             Resource[];
  existingBookings:      Booking[];
  requestSkills:         string[];
  slotEdits:             SlotEdits;
  onSelect:              () => void;
  onSlotEdit:            (slotId: string, patch: SlotEdit) => void;
  onSlotReset:           (slotId: string) => void;
  onPreviewOnCalendar?:  (scenario: PlanScenario) => void;
}

function ScenarioCard({
  scenario, isSelected, isAccepted,
  resources, existingBookings, requestSkills, slotEdits,
  onSelect, onSlotEdit, onSlotReset, onPreviewOnCalendar,
}: ScenarioCardProps) {
  const [expanded,  setExpanded]  = useState(false);
  const [editMode,  setEditMode]  = useState(false);
  const meta = STRATEGY_META[scenario.strategy];

  const slotsByMonth = useMemo(() => {
    const map = new Map<string, ProposedBookingSlot[]>();
    for (const slot of scenario.slots) {
      if (!map.has(slot.month)) map.set(slot.month, []);
      map.get(slot.month)!.push(slot);
    }
    return map;
  }, [scenario.slots]);

  const editCount = useMemo(
    () => scenario.slots.filter(s => slotEdits.has(s.id) && Object.keys(slotEdits.get(s.id)!).length > 0).length,
    [scenario.slots, slotEdits],
  );

  function handleCardClick() {
    if (!expanded) onSelect();
    else onSelect();
  }

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      onClick={handleCardClick}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && handleCardClick()}
      className={cn(
        'rounded-xl border bg-card transition-all duration-200 cursor-pointer outline-none select-none',
        meta.accent,
        isSelected
          ? `ring-2 ${meta.ringAccent} border-[var(--accent-border)] shadow-md`
          : 'hover:shadow-sm hover:border-[var(--accent-border)]/60',
        isAccepted && 'pointer-events-none',
      )}
    >
      {/* ── Top section ── */}
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border"
            style={{
              backgroundColor: 'var(--accent-bg-outline)',
              borderColor:     'var(--accent-border)',
              color:           'var(--accent-text-on-outline)',
            }}
          >
            {meta.icon}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-semibold">{scenario.label}</h3>
              {meta.recommended && !isAccepted && (
                <Badge variant="accent" accent="teal" className="text-[10px] px-1.5 py-0 h-4 gap-1">
                  <Star className="h-2.5 w-2.5 fill-current" />
                  Recommended
                </Badge>
              )}
              {isSelected && !isAccepted && (
                <Badge variant="default" className="text-[10px] px-1.5 py-0 h-4 gap-1">
                  <Check className="h-2.5 w-2.5" />
                  Selected
                </Badge>
              )}
              {isAccepted && (
                <Badge variant="accent" accent="teal" className="text-[10px] px-1.5 py-0 h-4 gap-1">
                  <CheckCircle2 className="h-2.5 w-2.5" />
                  Accepted
                </Badge>
              )}
              {editCount > 0 && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 gap-1">
                  <Pencil className="h-2.5 w-2.5" />
                  {editCount} edit{editCount !== 1 ? 's' : ''}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{scenario.description}</p>
          </div>

          <div className="shrink-0 text-right flex gap-4">
            <div>
              <div className="text-lg font-bold tabular-nums leading-none">{scenario.totalHours}h</div>
              <div className="text-[10px] text-muted-foreground">covered</div>
            </div>
            <div>
              <div className="text-lg font-bold tabular-nums leading-none">{scenario.resourceCount}</div>
              <div className="text-[10px] text-muted-foreground">{scenario.resourceCount === 1 ? 'person' : 'people'}</div>
            </div>
          </div>
        </div>

        {/* Coverage bar */}
        <div className="mt-4 space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
              Capacity coverage
            </span>
            <span className="text-xs font-bold tabular-nums" style={{ color: 'var(--accent-text-on-light)' }}>
              {scenario.coveragePercent}%
            </span>
          </div>
          <Progress value={scenario.coveragePercent} className="h-1.5" />
        </div>

        {/* Date range */}
        <div className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
          <CalendarDays className="h-3.5 w-3.5 shrink-0" />
          <span>{fmtDate(scenario.earliestStart)}</span>
          <ArrowRight className="h-3 w-3 shrink-0" />
          <span>{fmtDate(scenario.latestEnd)}</span>
        </div>

        {/* Action buttons — always visible when card is selected */}
        {isSelected && !isAccepted && (
          <div className="mt-3 flex items-center gap-2" onClick={e => e.stopPropagation()}>
            {onPreviewOnCalendar && (
              <PreviewButton scenario={scenario} onPreview={onPreviewOnCalendar} />
            )}
            <Button
              variant={editMode ? 'default' : 'outline'}
              size="sm"
              className="h-6 text-[10px] px-2 gap-1"
              onClick={() => {
                const next = !editMode;
                setEditMode(next);
                if (next) setExpanded(true);
              }}
            >
              {editMode ? (
                <><Check className="h-3 w-3" />Done</>
              ) : (
                <><Pencil className="h-3 w-3" />Adjust bookings</>
              )}
            </Button>
          </div>
        )}
      </div>

      {/* ── Pros / Cons ── */}
      <div className="px-4 pb-4 grid grid-cols-2 gap-4 border-t pt-3">
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <ThumbsUp className="h-3 w-3" style={{ color: 'var(--accent-text-on-outline)' }} />
            <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Pros</span>
          </div>
          <ul className="space-y-1.5">
            {scenario.pros.map((p, i) => (
              <li key={i} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                <span className="mt-1.5 h-1 w-1 rounded-full shrink-0" style={{ backgroundColor: 'var(--accent-text-on-outline)' }} />
                {p}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <ThumbsDown className="h-3 w-3 text-muted-foreground/50" />
            <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Cons</span>
          </div>
          <ul className="space-y-1.5">
            {scenario.cons.map((c, i) => (
              <li key={i} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                <span className="mt-1.5 h-1 w-1 rounded-full bg-muted-foreground/30 shrink-0" />
                {c}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* ── Proposed bookings toggle ── */}
      <div className="border-t" onClick={e => e.stopPropagation()}>
        <button
          type="button"
          className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-accent/20 transition-colors"
          onClick={() => setExpanded(x => !x)}
          aria-expanded={expanded}
        >
          <span className="flex items-center gap-1.5">
            <CalendarDays className="h-3.5 w-3.5" />
            {scenario.slots.length} proposed booking{scenario.slots.length !== 1 ? 's' : ''}
            {editCount > 0 && (
              <span className="text-primary font-semibold">· {editCount} edited</span>
            )}
          </span>
          <ChevronDown className={cn('h-4 w-4 transition-transform duration-200', expanded && 'rotate-180')} />
        </button>

        {expanded && (
          <div className="px-3 pb-3 space-y-4">

            {/* Edit mode toolbar */}
            {isSelected && !isAccepted && (
              <div className={cn(
                'flex items-center justify-between rounded-lg px-3 py-2 border',
                editMode
                  ? 'bg-primary/5 border-primary/20'
                  : 'bg-muted/30 border-border/40',
              )}>
                <div className="flex items-center gap-2">
                  {editMode ? (
                    <>
                      <Pencil className="h-3.5 w-3.5 text-primary shrink-0" />
                      <span className="text-xs font-medium text-primary">
                        Editing bookings — adjust resource, dates or hours
                      </span>
                    </>
                  ) : (
                    <>
                      <Info className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="text-xs text-muted-foreground">
                        Review the suggested bookings below
                      </span>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {editMode && editCount > 0 && (
                    <button
                      type="button"
                      className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
                      onClick={() => {
                        scenario.slots.forEach(s => onSlotReset(s.id));
                      }}
                    >
                      <RotateCcw className="h-3 w-3" />
                      Reset all
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Slot list grouped by month */}
            {Array.from(slotsByMonth.entries()).map(([month, slots]) => (
              <div key={month} className="space-y-1.5">
                <div className="px-1 text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--accent-text-on-outline)' }}>
                  {month}
                </div>
                {slots.map(slot => (
                  <SlotRow
                    key={slot.id}
                    slot={slot}
                    edit={slotEdits.get(slot.id)}
                    resources={resources}
                    existingBookings={existingBookings}
                    requestSkills={requestSkills}
                    isEditing={editMode && isSelected && !isAccepted}
                    onEdit={onSlotEdit}
                    onReset={onSlotReset}
                  />
                ))}
              </div>
            ))}

            {/* Edit hint when not selected */}
            {!isSelected && (
              <p className="text-center text-[10px] text-muted-foreground py-1">
                Select this scenario to adjust bookings
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────────

function GeneratingSkeleton() {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-5 text-center">
      <div className="relative">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 border border-primary/20">

        </div>
        <div className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center rounded-full bg-primary">
          <Loader2 className="h-3 w-3 text-primary-foreground animate-spin" />
        </div>
      </div>
      <div>
        <p className="text-sm font-semibold">Generating planning scenarios…</p>
        <p className="text-xs text-muted-foreground mt-1.5 max-w-xs leading-relaxed">
          Analysing resource availability, skills match and capacity across your team
        </p>
      </div>
      <div className="w-full space-y-3 mt-2">
        {[1, 2, 3].map(i => (
          <div key={i} className="rounded-xl border bg-muted/30 p-4 animate-pulse" style={{ animationDelay: `${i * 150}ms` }}>
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-xl bg-muted/60 shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-3.5 w-32 rounded bg-muted/60" />
                <div className="h-3 w-48 rounded bg-muted/40" />
              </div>
            </div>
            <div className="mt-4 h-1.5 rounded-full bg-muted/60" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Budget health colour helpers (EDS semantic) ─────────────────────────────

const HEALTH_COLORS: Record<'teal' | 'warning' | 'destructive', {
  progress: string;
  text: string;
  bg: string;
}> = {
  teal:        { progress: 'bg-[var(--accent-teal-500)]',        text: 'text-[var(--accent-teal-700)]',        bg: 'bg-[var(--accent-teal-50)]' },
  warning:     { progress: 'bg-[var(--accent-warning-500)]',     text: 'text-[var(--accent-warning-700)]',     bg: 'bg-[var(--accent-warning-50)]' },
  destructive: { progress: 'bg-destructive',                     text: 'text-destructive',                     bg: 'bg-destructive/5' },
};

// ── Project Budget Context panel (for left panel) ─────────────────────────────

interface ProjectBudgetPanelProps {
  request: ResourceRequest;
  budgetData: EBProjectsBudgetComposite | null;
  budgetLoading: boolean;
  budgetError: string | null;
  roleBudgets: EBProjectsBudgetComposite['roleBudgets'];
}

function ProjectBudgetPanel({
  request,
  budgetData,
  budgetLoading,
  budgetError,
  roleBudgets,
}: ProjectBudgetPanelProps) {
  if (budgetLoading) {
    return (
      <div className="space-y-2">
        <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Project Resource Budget</div>
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-2 w-full" />
      </div>
    );
  }

  if (budgetError || !budgetData) {
    return (
      <div className="space-y-2">
        <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Project Resource Budget</div>
        <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/40 rounded-lg px-3 py-2.5 border border-dashed border-border/60">
          <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span className="leading-relaxed">No resource budget configured for this project</span>
        </div>
      </div>
    );
  }

  const matchedRole = findRoleBudget(roleBudgets, request.role);

  if (!matchedRole) {
    return (
      <div className="space-y-2">
        <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Project Resource Budget</div>
        <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/40 rounded-lg px-3 py-2.5 border border-dashed border-border/60">
          <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span className="leading-relaxed">No budget for {request.role} role in this project</span>
        </div>
      </div>
    );
  }

  const health = budgetHealthAccent(matchedRole.percentUsed);
  const colors = HEALTH_COLORS[health];
  const monthlyBudget = parseMonthlyHours(matchedRole.monthlyHours);
  const demandHints = request.monthlyHourHints ?? {};

  // Build merged month keys from both budget and demand hints
  const allMonths = Array.from(
    new Set([...Object.keys(monthlyBudget), ...Object.keys(demandHints)]),
  ).sort();

  return (
    <div className="space-y-3">
      <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
        Project Resource Budget
      </div>

      {/* Role summary */}
      <div className="rounded-lg border border-border/50 bg-muted/20 px-3 py-2.5 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium truncate">{matchedRole.roleName}</span>
          <Badge variant="accent" accent={health as any} className="text-[10px] px-1.5 py-0 h-4 shrink-0">
            {Math.round(matchedRole.percentUsed)}% used
          </Badge>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <div className="text-[10px] text-muted-foreground">Budget</div>
            <div className="text-xs font-bold tabular-nums">{matchedRole.budgetHours}h</div>
          </div>
          <div>
            <div className="text-[10px] text-muted-foreground">Consumed</div>
            <div className="text-xs font-bold tabular-nums">{matchedRole.actualHours}h</div>
          </div>
          <div>
            <div className="text-[10px] text-muted-foreground">Remaining</div>
            <div className={cn('text-xs font-bold tabular-nums', colors.text)}>{matchedRole.remainingHours}h</div>
          </div>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div
            className={cn('h-full rounded-full transition-all', colors.progress)}
            style={{ width: `${Math.min(100, matchedRole.percentUsed)}%` }}
          />
        </div>
      </div>

      {/* Monthly breakdown comparison */}
      {allMonths.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
            Monthly Breakdown
          </div>
          <div className="rounded-lg border border-border/50 overflow-hidden">
            {/* Header */}
            <div className="grid grid-cols-5 gap-0 px-2 py-1.5 bg-muted/40 border-b border-border/40">
              <span className="text-[9px] font-semibold text-muted-foreground uppercase">Month</span>
              <span className="text-[9px] font-semibold text-muted-foreground uppercase text-right">Budg.</span>
              <span className="text-[9px] font-semibold text-muted-foreground uppercase text-right">Used</span>
              <span className="text-[9px] font-semibold text-muted-foreground uppercase text-right">Avail.</span>
              <span className="text-[9px] font-semibold text-muted-foreground uppercase text-right">Req.</span>
            </div>
            {/* Rows */}
            {allMonths.map((month) => {
              const budgeted = monthlyBudget[month] ?? 0;
              // For consumed we don't have per-month consumed data from the API,
              // so we pro-rate using the overall ratio.
              const ratio = matchedRole.budgetHours > 0
                ? matchedRole.actualHours / matchedRole.budgetHours
                : 0;
              const consumed = Math.round(budgeted * ratio);
              const available = Math.max(0, budgeted - consumed);
              const requested = demandHints[month] ?? 0;
              const overBudget = requested > 0 && requested > available;

              const [yr, mo] = month.split('-').map(Number);
              const label = new Date(yr, mo - 1).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });

              return (
                <div
                  key={month}
                  className={cn(
                    'grid grid-cols-5 gap-0 px-2 py-1 border-b border-border/20 last:border-b-0',
                    overBudget && 'bg-destructive/5',
                  )}
                >
                  <span className="text-[10px] text-muted-foreground">{label}</span>
                  <span className="text-[10px] tabular-nums text-right">{budgeted}h</span>
                  <span className="text-[10px] tabular-nums text-right text-muted-foreground">{consumed}h</span>
                  <span className="text-[10px] tabular-nums text-right">{available}h</span>
                  <span className={cn(
                    'text-[10px] tabular-nums text-right font-medium',
                    overBudget ? 'text-destructive' : 'text-foreground',
                  )}>
                    {requested > 0 ? `${requested}h` : '-'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Left panel — request summary ──────────────────────────────────────────────

interface RequestSummaryPanelProps {
  request: ResourceRequest;
  budgetData: EBProjectsBudgetComposite | null;
  budgetLoading: boolean;
  budgetError: string | null;
  roleBudgets: EBProjectsBudgetComposite['roleBudgets'];
}

function RequestSummaryPanel({ request, budgetData, budgetLoading, budgetError, roleBudgets }: RequestSummaryPanelProps) {
  const priorityAccent = request.priority === 'critical' ? 'destructive'
    : request.priority === 'high'   ? 'warning'
    : request.priority === 'medium' ? 'info'
    : 'teal';

  return (
    <div className="flex flex-col gap-5 h-full">
      <div>
        <div className="flex items-start justify-between gap-2 mb-1">
          <h2 className="text-base font-bold leading-tight">{request.projectName}</h2>
          <Badge variant="accent" accent={priorityAccent as any} className="shrink-0 capitalize text-xs">
            {request.priority}
          </Badge>
        </div>
        {request.projectCode && (
          <span className="text-[10px] font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
            {request.projectCode}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Building2 className="h-3.5 w-3.5 shrink-0" />
        <span>
          Requested by{' '}
          <span className="font-medium text-foreground">{request.requestedByName}</span>
        </span>
      </div>

      <Separator />

      <div className="grid grid-cols-1 gap-3">
        <div className="flex items-center gap-3 rounded-lg bg-muted/40 px-3 py-2.5">
          <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
          <div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Total Hours</div>
            <div className="text-xl font-bold tabular-nums leading-tight">{request.totalHoursRequested}h</div>
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-lg bg-muted/40 px-3 py-2.5">
          <User className="h-4 w-4 text-muted-foreground shrink-0" />
          <div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Role Needed</div>
            <div className="text-sm font-semibold leading-tight">{request.role}</div>
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-lg bg-muted/40 px-3 py-2.5">
          <CalendarDays className="h-4 w-4 text-muted-foreground shrink-0" />
          <div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Start Date</div>
            <div className="text-sm font-semibold leading-tight">{fmtDate(request.requestedStartDate)}</div>
          </div>
        </div>
      </div>

      {request.monthlyHourHints && Object.keys(request.monthlyHourHints).length > 0 && (
        <div>
          <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            Monthly Spread (hint)
          </div>
          <div className="space-y-1.5">
            {Object.entries(request.monthlyHourHints).map(([month, hours]) => (
              <div key={month} className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{month}</span>
                <span className="font-medium tabular-nums">{hours}h</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Project Resource Budget ── */}
      <ProjectBudgetPanel
        request={request}
        budgetData={budgetData}
        budgetLoading={budgetLoading}
        budgetError={budgetError}
        roleBudgets={roleBudgets}
      />

      <Separator />

      {request.skills.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <Tag className="h-3 w-3 text-muted-foreground" />
            <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
              Required Skills
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {request.skills.map(s => (
              <Badge key={s} variant="secondary" className="text-xs px-2 py-0.5">{s}</Badge>
            ))}
          </div>
        </div>
      )}

      {request.notes && (
        <div className="flex-1">
          <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">Notes</div>
          <p className="text-xs text-muted-foreground leading-relaxed border-l-2 border-border pl-3 italic">
            "{request.notes}"
          </p>
        </div>
      )}
    </div>
  );
}

// ── Main modal ────────────────────────────────────────────────────────────────

export interface ResourceRequestPlanDrawerProps {
  open:                  boolean;
  onOpenChange:          (open: boolean) => void;
  request:               ResourceRequest | null;
  resources:             Resource[];
  existingBookings:      Booking[];
  scenarios:             PlanScenario[];
  isGenerating:          boolean;
  onAccept:              (scenarioId: string, edits: SlotEdits) => Promise<void>;
  /** Called when user clicks "Preview on Calendar" for a scenario */
  onPreviewOnCalendar?:  (preview: PlanPreviewScenario) => void;
}

export function ResourceRequestPlanDrawer({
  open,
  onOpenChange,
  request,
  resources,
  existingBookings,
  scenarios,
  isGenerating,
  onAccept,
  onPreviewOnCalendar,
}: ResourceRequestPlanDrawerProps) {
  const [selectedScenarioId, setSelectedScenarioId] = useState<string | null>(null);
  const [isAccepting, setIsAccepting]               = useState(false);
  // Per-slot edits: keyed by slot ID, isolated per scenario selection
  const [slotEdits, setSlotEdits]                   = useState<SlotEdits>(new Map());

  const defaultScenario = useMemo(() => {
    if (!scenarios.length) return null;
    return scenarios.find(s => s.strategy === 'spread-load') ?? scenarios[0];
  }, [scenarios]);

  const effectiveSelected = selectedScenarioId ?? defaultScenario?.id ?? null;
  const selectedScenario  = scenarios.find(s => s.id === effectiveSelected);

  // Reset edits when a different scenario is selected
  function handleSelectScenario(id: string) {
    if (id !== effectiveSelected) {
      setSlotEdits(new Map());
    }
    setSelectedScenarioId(id);
  }

  const handleSlotEdit = useCallback((slotId: string, patch: SlotEdit) => {
    setSlotEdits(prev => {
      const next = new Map(prev);
      next.set(slotId, { ...(next.get(slotId) ?? {}), ...patch });
      return next;
    });
  }, []);

  const handleSlotReset = useCallback((slotId: string) => {
    setSlotEdits(prev => {
      const next = new Map(prev);
      next.delete(slotId);
      return next;
    });
  }, []);

  const totalEditCount = useMemo(
    () => Array.from(slotEdits.values()).filter(e => Object.keys(e).length > 0).length,
    [slotEdits],
  );

  async function handleAccept() {
    if (!effectiveSelected) return;
    setIsAccepting(true);
    try {
      await onAccept(effectiveSelected, slotEdits);
      onOpenChange(false);
    } catch (_e) {
      // handle silently
    } finally {
      setIsAccepting(false);
    }
  }

  function handlePreviewOnCalendar(scenario: PlanScenario) {
    if (!request || !onPreviewOnCalendar) return;
    onPreviewOnCalendar({
      scenario,
      requestId: request.id,
      requestProjectName: request.projectName,
      requestRole: request.role,
    });
    // Close the drawer so the calendar is fully visible
    onOpenChange(false);
  }

  // ── Budget data for the left panel ─────────────────────────────────────────
  const { budgetData, budgetLoading, budgetError, roleBudgets } = useProjectBudget(
    request?.projectId,
  );

  if (!request) return null;

  const isAccepted = request.status === 'accepted';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'p-0 gap-0 overflow-hidden',
          'w-[90vw] max-w-5xl',
          'h-[88vh] max-h-[860px]',
          'flex flex-col',
        )}
      >
        {/* ── Header bar ── */}
        <div className="flex items-center gap-3 px-6 py-4 border-b shrink-0 bg-muted/20">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 border border-primary/20">
          </div>
          <div className="flex-1 min-w-0">
            <DialogTitle className="text-sm font-semibold leading-none">
              Suggested Project Plan
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground mt-0.5">
              Review scenarios, adjust any booking then accept to create them
            </DialogDescription>
          </div>
          {!isGenerating && scenarios.length > 0 && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="accent" accent="teal" className="text-xs gap-1 cursor-help shrink-0">
                    <Star className="h-3 w-3 fill-current" />
                    Spread the Load recommended
                  </Badge>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs max-w-[220px]">
                  Spreading load reduces single points of failure and better utilises team capacity.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        {/* ── Two-panel body ── */}
        <div className="flex flex-1 min-h-0 overflow-hidden">

          {/* Left — request summary */}
          <div className="w-72 shrink-0 border-r bg-muted/10 overflow-y-auto px-5 py-5">
            <RequestSummaryPanel
              request={request}
              budgetData={budgetData}
              budgetLoading={budgetLoading}
              budgetError={budgetError}
              roleBudgets={roleBudgets}
            />
          </div>

          {/* Right — scenarios */}
          <div className="flex-1 min-w-0 overflow-y-auto px-6 py-5 space-y-4">
            {isGenerating ? (
              <GeneratingSkeleton />
            ) : scenarios.length === 0 ? (
              <div className="py-8">
                <Alert variant="warning">
                  <AlertTitle>No scenarios could be generated</AlertTitle>
                  <AlertDescription>
                    There may not be enough resource capacity for the requested dates.
                    Try adjusting the start date or reducing the total hours.
                  </AlertDescription>
                </Alert>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    {scenarios.length} scenario{scenarios.length !== 1 ? 's' : ''} generated — select one to review and adjust
                  </span>
                </div>

                {scenarios.map(scenario => (
                  <ScenarioCard
                    key={scenario.id}
                    scenario={scenario}
                    isSelected={effectiveSelected === scenario.id}
                    isAccepted={isAccepted && request.acceptedScenarioId === scenario.id}
                    resources={resources}
                    existingBookings={existingBookings}
                    requestSkills={request.skills}
                    slotEdits={slotEdits}
                    onSelect={() => handleSelectScenario(scenario.id)}
                    onSlotEdit={handleSlotEdit}
                    onSlotReset={handleSlotReset}
                    onPreviewOnCalendar={onPreviewOnCalendar ? handlePreviewOnCalendar : undefined}
                  />
                ))}

                {isAccepted && (
                  <Alert variant="success" size="compact">
                    <AlertTitle>Plan accepted</AlertTitle>
                    <AlertDescription>
                      Bookings have been created from the accepted scenario.
                    </AlertDescription>
                  </Alert>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="border-t bg-card px-6 py-3.5 shrink-0 flex items-center justify-between gap-4">
          <div className="text-xs text-muted-foreground">
            {isAccepted ? (
              <span className="flex items-center gap-1.5 font-medium" style={{ color: 'var(--accent-teal-600)' }}>
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                Plan accepted — bookings created successfully
              </span>
            ) : selectedScenario ? (
              <span className="flex items-center gap-2 flex-wrap">
                <span>
                  <span className="font-medium text-foreground">{selectedScenario.label}</span>
                  {' — '}
                  <span className="font-medium text-foreground">
                    {selectedScenario.slots.length} booking{selectedScenario.slots.length !== 1 ? 's' : ''}
                  </span>
                  {' across '}
                  <span className="font-medium text-foreground">
                    {selectedScenario.resourceCount} resource{selectedScenario.resourceCount !== 1 ? 's' : ''}
                  </span>
                </span>
                {totalEditCount > 0 && (
                  <Badge variant="secondary" className="text-[10px] gap-1 h-5">
                    <Pencil className="h-2.5 w-2.5" />
                    {totalEditCount} booking{totalEditCount !== 1 ? 's' : ''} adjusted
                  </Badge>
                )}
              </span>
            ) : (
              <span className="text-muted-foreground/60">Select a scenario above to accept</span>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={isAccepting}>
              Cancel
            </Button>
            {!isAccepted && (
              <Button
                size="sm"
                onClick={handleAccept}
                disabled={!effectiveSelected || isAccepting}
                className="gap-1.5"
              >
                {isAccepting ? (
                  <><Loader2 className="h-3.5 w-3.5 animate-spin" />Creating bookings…</>
                ) : totalEditCount > 0 ? (
                  <><Check className="h-3.5 w-3.5" />Accept adjusted plan</>
                ) : (
                  <><Check className="h-3.5 w-3.5" />Accept plan</>
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
